import React, { useEffect, useState } from "react";
import { Input, Skeleton } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../service/store";
import { useQueryWorkBentchMutation } from "../../service/apiSlices/sematicModel";
import { showErrorToast, showSuccessToast } from "../../utilities/toastUtils";
import { setSemanticModelName, setSemanticSecretKey } from "../../service/apiSlices/staticApi";
import { REGEX_PATTERNS } from "../../utilities/common";
import './styles.css'
import Logo from "../../assets/logo.svg";
import Logotext from "../../assets/logoText.svg";
import { TOKENS } from "../../utilities/Constants/Constants";
import useDebounce from "../../hooks/useDebounce";
import { debounce } from "lodash";
import { handleWorkbenchNameInput } from "../../utilities/utils";
import { useWorkbenchContext } from "../../context/WorkbenchContext";
import UserProfile from "../UserProfile";

const defaultConfig = {
  isTitleEditable: true,
}

const SemanticHeader = ({ config }: { config?: { isTitleEditable?: boolean } }) => {
  const updatedConfig = { ...defaultConfig, ...config };

  const { sk } = useParams()
  const location = useLocation();

  const connectionId = sessionStorage.getItem(TOKENS.CONNECTION_ID);
  const semanticData = sessionStorage.getItem("SemanticData");
  const parsedData = semanticData ? JSON.parse(semanticData) : null;
  const secretKey = sk || parsedData?.secret_key || location?.state?.Id || "";

  const { t } = useTranslation("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const modelTitle = useSelector(
    (state: RootState) => state?.static.SemanticModelName
  );
  const { workBenchData, updateWorkBenchData, isWorkbenchLoading } = useWorkbenchContext();

  const [editable, setEditable] = useState(false);
  const [title, setTitle] = useState(modelTitle || workBenchData?.workbench_name);

  const [createQueryBentch] = useQueryWorkBentchMutation();

  const debouncedTitle = useDebounce(title, 1000)

  useEffect(() => {
    if (workBenchData?.workbench_name) {
      setTitle(workBenchData.workbench_name);
    } else {
      setTitle('')
    }
  }, [workBenchData?.workbench_name]);

  useEffect(() => {
    if (!title) return;
    if (title === workBenchData?.workbench_name) return;
    handleOnModalTitleBlur()
  }, [debouncedTitle])

  const allowOnlyAlphaNumericAndSpaces = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (!REGEX_PATTERNS.alphaNumericWithSpacesRegex.test(event.key)) {
      event.preventDefault();
      showErrorToast("Only letters, numbers, and spaces are allowed.");
    }
  };

  const handleRenameWorkbench = () => {
    if (!updatedConfig.isTitleEditable) return;
    setEditable(true);
  };

  const handleModelTitleChange = (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const newName = event?.target?.value?.trim() || "";
    setTitle(() => newName);
  };

  const handleOnModalTitleBlur = () => {
    if (!title || (title && title.length < 3)) {
      showErrorToast("QueryCard name must be at least 3 characters long.");
      return;
    }

    if (title === workBenchData?.workbench_name) {
      return setEditable(false)
    };

    if (title) {
      const payload = {
        format_type: 5,
        workbench_name: title || "Untitled",
        key: secretKey || '',
        connection_key: connectionId,
      };

      createQueryBentch(payload)
        .unwrap()
        .then((response: any) => {
          const { workbench_name, ...restResponse } = response?.data;
          sessionStorage.setItem("SemanticData", JSON.stringify(restResponse));

          if (response.status_code === 200) {
            setEditable(false);

            // if (response?.secret_key) navigate(`${ROUTES.SEMANTIC_MODEL}/${response.secret_key}`, {replace: true})
            if (workbench_name) {
              dispatch(setSemanticModelName(workbench_name));
              dispatch(setSemanticSecretKey(restResponse?.secret_key));
              updateWorkBenchData(response.data)
              showSuccessToast(response?.message);
            }
          } else if (response.status_code === 400) {
            showErrorToast(response?.message || 'Something went wrong. Try after sometime.');
          }
        })
        .catch((err: any) => {
          showErrorToast(err.data?.message);
        });
    }
  }

  const handleOnBlur = () => {
    setTitle(workBenchData?.workbench_name)
    setEditable(false)
  }

  return (
    <div className="header">
      <div className="row">
        <div className="col-md-12 d-flex ">
          <div className="card header-content-card w-100">
            <div className="card-body">
              <div
                className="d-flex gap-2 align-items-center justify-content-between"
                style={{ height: "24px" }}
              >
                <div className="d-flex align-items-center gap-4">
                  <div className="d-flex gap-2 align-items-center" onClick={() => navigate('/dashboard')}>
                    <img src={Logo} alt='logo' className="main-logo" width={30} />
                    <img src={Logotext} alt='Logotext' className="logo-text" width={40} />
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <p
                    className="mb-0 workbench-title"
                    style={editable ? { width: "auto" } : { width: "auto" }}
                  >
                    Model Name :
                  </p>
                  {editable ? (
                    <div className="inputWrapper">
                      <Input
                        type="text"
                        className="name-input"
                        maxLength={100}
                        onBlur={handleOnBlur}
                        onChange={handleModelTitleChange}
                        defaultValue={title}
                        placeholder={'Enter model name'}
                        autoFocus
                        onKeyPress={allowOnlyAlphaNumericAndSpaces}
                        onInput={handleWorkbenchNameInput}
                      />
                    </div>
                  ) : (
                    <p
                      onClick={handleRenameWorkbench}
                      className="sub-title mb-0"
                    >
                      {(isWorkbenchLoading && !title) ? <Skeleton.Input active size="small" style={{ width: 100 }} /> : (title || "Untitled name")}
                    </p>
                  )}
                </div>

                <div className="d-flex flex-row gap-3">
                  <UserProfile />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SemanticHeader;
